import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, QrCode, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from "@tabipla/ui";
import { useMyFriendCode } from "@/application/hooks/useFriends";

interface FriendCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FriendCodeDialog({ open, onOpenChange }: FriendCodeDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { data: myCode, isLoading, error } = useMyFriendCode();

  const handleCopy = async () => {
    if (!myCode?.friendCode) return;

    try {
      await navigator.clipboard.writeText(myCode.friendCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = myCode.friendCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            {t("friends.myCode.title", "マイフレンドコード")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "friends.myCode.description",
              "このコードを友達に共有すると、友達に追加してもらえます"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {t("friends.myCode.error", "コードの取得に失敗しました")}
            </div>
          ) : (
            <>
              {/* QR Code Display */}
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-xl bg-white p-3">
                  <QRCodeSVG value={myCode?.friendCode ?? ""} size={140} level="M" />
                </div>
                <div className="flex items-center gap-2">
                  <code className="rounded-lg bg-muted px-4 py-2 font-mono text-xl font-bold tracking-wider">
                    {myCode?.friendCode}
                  </code>
                  <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              <p className="text-center text-sm text-muted-foreground">
                {t(
                  "friends.myCode.scanInstruction",
                  "このQRコードを友達にスキャンしてもらってください"
                )}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
